import axios, { AxiosError, AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { API_DEFAULT_HEADER, API_TIMEOUT, TOKEN_KEY } from '@/config/app.config';

const REFRESH_TOKEN_KEY = 'vldl_refresh_token';

const AXIOS_DEFAULT_CONFIG = {
  baseURL: import.meta.env.VITE_API_URL || '/api/v2',
  headers: API_DEFAULT_HEADER,
  timeout: API_TIMEOUT,
};

export const rawAxiosApi = axios.create(AXIOS_DEFAULT_CONFIG);
export const httpApiClient = axios.create(AXIOS_DEFAULT_CONFIG);

httpApiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

let isRefreshing = false;
let requestQueue: ((token: string) => void)[] = [];

const processQueue = (token: string | null) => {
  requestQueue.forEach((cb) => {
    if (token) cb(token);
  });
  requestQueue = [];
};

httpApiClient.interceptors.response.use(
  (response: AxiosResponse) => response.data,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Network/timeout errors
    if (!error.response) {
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        status: null,
      });
    }

    const { status } = error.response;

    // 🔁 Handle 401 (token expired)
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue the request until token refresh completes
        return new Promise((resolve) => {
          requestQueue.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(httpApiClient(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Use rawAxiosApi to avoid interceptor loop
        const response = await rawAxiosApi.post('/auth/refresh', { refreshToken });
        const data = response.data?.data || response.data;

        if (!data?.accessToken) {
          throw new Error('Invalid refresh response');
        }

        const newToken = data.accessToken;

        // Update tokens in localStorage
        localStorage.setItem(TOKEN_KEY, newToken);
        if (data.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        }

        // Update auth store
        useAuthStore.getState().setToken(newToken);

        // Process queued requests with new token
        processQueue(newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return httpApiClient(originalRequest);
      } catch (refreshError) {
        // Clear queue and logout on refresh failure
        processQueue(null);
        useAuthStore.getState().logout();

        // Redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ❌ Other errors
    const message =
      (error.response.data as any)?.message ||
      (error.response.data as any)?.error ||
      `Request failed with status ${status}`;

    return Promise.reject({
      message,
      status,
      data: error.response.data,
    });
  }
);

// Exported for use in other parts of the app if needed
export const authAPI = {
  refresh: async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('No refresh token');

    const response = await rawAxiosApi.post('/auth/refresh', { refreshToken });
    const data = response.data?.data || response.data;

    if (!data?.accessToken) throw new Error('Invalid refresh response');

    // Update both tokens
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    }

    return data.accessToken;
  },
};
